/**
 * Generates a deterministic organic pattern SVG based on an ID.
 * The pattern will always be the same for the same ID.
 */

interface ColorPalette {
  primary: string;
  secondary: string;
}

// Predefined color palettes for organic patterns
const COLOR_PALETTES: ColorPalette[] = [
  { primary: '#ff6b9d', secondary: '#c44569' }, // Magenta/Green
  { primary: '#a1c4fd', secondary: '#ffb3d9' }, // Blue/Pink
  { primary: '#ffb347', secondary: '#ff6b35' }, // Orange/Red-Orange
  { primary: '#ff9a9e', secondary: '#fecfef' }, // Pink/Pink
  { primary: '#667eea', secondary: '#764ba2' }, // Purple/Purple
  { primary: '#f093fb', secondary: '#f5576c' }, // Pink/Red
  { primary: '#4facfe', secondary: '#00f2fe' }, // Blue/Cyan
  { primary: '#43e97b', secondary: '#38f9d7' }, // Green/Cyan
  { primary: '#fa709a', secondary: '#fee140' }, // Pink/Yellow
  { primary: '#30cfd0', secondary: '#330867' }, // Cyan/Dark Purple
  { primary: '#ff6b6b', secondary: '#4ecdc4' }, // Red/Cyan
  { primary: '#a8edea', secondary: '#fed6e3' }, // Light Cyan/Pink
];

/**
 * Generates a hash from a string to get a deterministic number
 */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Generates a deterministic number between min and max based on ID and seed
 */
function seededRandom(id: string, seed: number, min: number, max: number): number {
  const hash = hashString(`${id}-${seed}`);
  const normalized = (hash % 10000) / 10000;
  return min + normalized * (max - min);
}

/**
 * Generates an organic pattern SVG data URL based on ID
 */
export function generateOrganicPattern(id: string): string {
  const hash = hashString(id);
  const paletteIndex = hash % COLOR_PALETTES.length;
  const palette = COLOR_PALETTES[paletteIndex];
  
  // Generate deterministic values for pattern
  const numBlobs = 3 + (hash % 4); // 3-6 blobs
  const baseRotation = seededRandom(id, 0, 0, 360);
  
  // Create SVG with organic blobs
  const svgParts: string[] = [];
  
  // Background
  svgParts.push(`<rect width="200" height="200" fill="${palette.primary}"/>`);
  
  // Generate organic blobs
  for (let i = 0; i < numBlobs; i++) {
    const cx = seededRandom(id, i * 10 + 1, 20, 180);
    const cy = seededRandom(id, i * 10 + 2, 20, 180);
    const rx = seededRandom(id, i * 10 + 3, 30, 80);
    const ry = seededRandom(id, i * 10 + 4, 30, 80);
    const rotation = seededRandom(id, i * 10 + 5, 0, 360);
    const opacity = seededRandom(id, i * 10 + 6, 0.4, 0.8);
    
    // Create organic blob using path with multiple curves
    const path = createOrganicBlob(cx, cy, rx, ry, rotation, id, i);
    
    svgParts.push(
      `<path d="${path}" fill="${palette.secondary}" opacity="${opacity.toFixed(2)}" filter="url(#blur)"/>`
    );
  }
  
  // Add some smaller accent blobs
  for (let i = 0; i < 2; i++) {
    const cx = seededRandom(id, i * 100 + 50, 30, 170);
    const cy = seededRandom(id, i * 100 + 51, 30, 170);
    const r = seededRandom(id, i * 100 + 52, 15, 40);
    const opacity = seededRandom(id, i * 100 + 53, 0.3, 0.6);
    
    svgParts.push(
      `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${palette.secondary}" opacity="${opacity.toFixed(2)}" filter="url(#blur)"/>`
    );
  }
  
  const svg = `
    <svg width="200" height="200" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="blur" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="8"/>
        </filter>
      </defs>
      ${svgParts.join('\n      ')}
    </svg>
  `.trim();
  
  // Convert to data URL
  const encoded = encodeURIComponent(svg);
  return `data:image/svg+xml,${encoded}`;
}

/**
 * Creates an organic blob path using Bezier curves
 */
function createOrganicBlob(
  centerX: number,
  centerY: number,
  radiusX: number,
  radiusY: number,
  rotation: number,
  id: string,
  seed: number
): string {
  const numPoints = 8;
  const points: Array<{ x: number; y: number }> = [];
  
  // Generate points around the ellipse
  for (let i = 0; i < numPoints; i++) {
    const angle = (i / numPoints) * Math.PI * 2;
    const variation = seededRandom(id, seed * 100 + i, 0.7, 1.3);
    const x = centerX + Math.cos(angle + rotation * Math.PI / 180) * radiusX * variation;
    const y = centerY + Math.sin(angle + rotation * Math.PI / 180) * radiusY * variation;
    points.push({ x, y });
  }
  
  // Create smooth path using quadratic Bezier curves
  let path = `M ${points[0].x} ${points[0].y}`;
  
  for (let i = 0; i < numPoints; i++) {
    const current = points[i];
    const next = points[(i + 1) % numPoints];
    const afterNext = points[(i + 2) % numPoints];
    
    // Control point for smooth curve
    const controlX = next.x;
    const controlY = next.y;
    const endX = (next.x + afterNext.x) / 2;
    const endY = (next.y + afterNext.y) / 2;
    
    path += ` Q ${controlX} ${controlY}, ${endX} ${endY}`;
  }
  
  path += ' Z';
  return path;
}

